import { useCallback, useMemo, useRef, useState } from "react";
import { Moment } from "moment";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useBoolean, useComponentState } from "@miplatform/hooks";
import { IAssignment, IUserListItemResponse } from "@miplatform/types";
import { isSameDay, subtractFromBusinessDate } from "@miplatform/utils";
import { useCities } from "../../../hooks/useCities/useCities";
import { useCommunities } from "../../../hooks/useCommunities/useCommunities";
import { TabGroupItem } from "../../NewHomeConstruction/NHCPriorityScheduler/CalendarVerticalTabs";
import {
  ASSIGNMENTS_ALL_OPTION,
  AssignmentUserSortBy,
  AssignmentsTab,
  AssignmentsUnassignedDisplayMode,
  AssignmentsUnassignedDisplayType,
  assignmentsTabGroups,
} from "./AssignmentsCalendar.constants";
import {
  AssignmentsTabGroup,
  IAssignmentCalendarUser,
} from "./AssignmentsCalendar.types";
import {
  countAssignments,
  getAssignmentsCalendarDefaultDate,
} from "./AssignmentsCalendar.util";

export const useAssignmentsCalendarViewModel = () => {
  const stateManager = useComponentState();
  const loadingAssignmentsRef = useRef<Record<string, boolean>>({});
  const [date, setDate] = useState<Moment>(getAssignmentsCalendarDefaultDate());
  const [activeTab, setActiveTab] = useState<AssignmentsTab>(
    AssignmentsTab.Unassigned
  );
  const [activeSubTab, setActiveSubTab] = useState<
    (TabGroupItem & { roleId?: string; roleName?: string }) | null
  >(null);
  const [filters, setFilters] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<IAssignment[]>([]);
  const [users, setUsers] = useState<IUserListItemResponse[]>([]);
  const [unassignedDisplayType, setUnassignedDisplayType] =
    useState<AssignmentsUnassignedDisplayType>(
      AssignmentsUnassignedDisplayType.City
    );
  const [unassignedDisplayMode, setUnassignedDisplayMode] =
    useState<AssignmentsUnassignedDisplayMode>(
      AssignmentsUnassignedDisplayMode.All
    );
  const [userSortBy, setUserSortBy] = useState<AssignmentUserSortBy>(
    AssignmentUserSortBy.Alphabetical
  );
  const [selectedUser, setSelectedUser] =
    useState<IAssignmentCalendarUser | null>(null);
  const [minHeight, setMinHeight] = useState(260);
  const [
    isCrewContainerCollapsed,
    handleCrewContainerCollapse,
    handleCrewContainerExpand,
  ] = useBoolean();
  const [
    isUnassignedContainerCollapsed,
    handleUnassignedContainerCollapse,
    handleUnassignedContainerExpand,
  ] = useBoolean();
  const { communities, getCommunities } = useCommunities();
  const { cities, getCities } = useCities();
  const {
    assignmentTemplates,
    unassignedAssignments,
    currentAssignedAssignments,
    currentPublishedAssignments,
    currentPublishedOrAssignedAssignments,
    roleTemplateMap,
  } = useMemo(() => {
    const assignmentTemplates: {
      label: string;
      value: string;
      roleId?: string;
      roleName?: string;
    }[] = [];
    const unassignedAssignments: IAssignment[] = [];
    const currentAssignedAssignments: IAssignment[] = [];
    const currentPublishedAssignments: IAssignment[] = [];
    const currentPublishedOrAssignedAssignments: IAssignment[] = [];

    const roleTemplateMap: Record<string, string[]> = {};
    assignments.forEach((assignment) => {
      const isCurrentDay = isSameDay(
        assignment.order?.effectiveScheduledDate,
        date
      );

      if (
        !assignmentTemplates.find(
          (t) => t.value === assignment.assignmentTemplateId
        )
      ) {
        assignmentTemplates.push({
          label: assignment.assignmentTemplateShortName,
          value: assignment.assignmentTemplateId as string,
          roleId: assignment.preferredRoleId,
          roleName: assignment.preferredRoleName,
        });
      }

      if (assignment.preferredRoleId) {
        if (!roleTemplateMap[assignment.preferredRoleId]) {
          roleTemplateMap[assignment.preferredRoleId] = [];
        }
        if (
          !roleTemplateMap[assignment.preferredRoleId].includes(
            assignment.assignmentTemplateId
          )
        ) {
          roleTemplateMap[assignment.preferredRoleId].push(
            assignment.assignmentTemplateId
          );
        }
      }

      if (!assignment.assigneeId && !assignment.publishedOn) {
        unassignedAssignments.push(assignment);
      }

      if (assignment.assigneeId && isCurrentDay) {
        currentAssignedAssignments.push(assignment);
      }

      if (assignment.publishedOn && isCurrentDay) {
        currentPublishedAssignments.push(assignment);
      }

      if ((assignment.assigneeId || assignment.publishedOn) && isCurrentDay) {
        currentPublishedOrAssignedAssignments.push(assignment);
      }
    });

    return {
      assignmentTemplates: assignmentTemplates.sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
      unassignedAssignments,
      currentAssignedAssignments,
      currentPublishedAssignments,
      currentPublishedOrAssignedAssignments,
      roleTemplateMap,
    };
  }, [date, assignments]);
  const filteredUnassignedAssignments = useMemo(() => {
    if (
      activeTab === AssignmentsTab.Unassigned &&
      activeSubTab &&
      activeSubTab?.value !== ASSIGNMENTS_ALL_OPTION
    ) {
      return unassignedAssignments.filter(
        (assignment) => assignment.assignmentTemplateId === activeSubTab.value
      );
    }

    return unassignedAssignments;
  }, [activeTab, activeSubTab, unassignedAssignments]);
  const filteredAssignments = useMemo(() => {
    if (activeTab === AssignmentsTab.Unassigned) {
      return currentPublishedOrAssignedAssignments;
    }

    const filter =
      activeTab === AssignmentsTab.Published
        ? (assignment: IAssignment) =>
            assignment.publishedOn &&
            assignment.assignmentTemplateId === activeSubTab?.value
        : (assignment: IAssignment) =>
            assignment.assignmentTemplateId === activeSubTab?.value;

    if (activeSubTab && activeSubTab?.value !== ASSIGNMENTS_ALL_OPTION) {
      return currentPublishedOrAssignedAssignments.filter(filter);
    }

    if (activeTab === AssignmentsTab.Published) {
      return currentPublishedOrAssignedAssignments.filter(
        (assignment) => assignment.publishedOn
      );
    }

    return currentPublishedOrAssignedAssignments;
  }, [activeTab, activeSubTab, currentPublishedOrAssignedAssignments]);
  const updatedUsers = useMemo(() => {
    const userMap = new Map<string, IAssignmentCalendarUser>();

    users.forEach((user) => {
      const { roleId, roleName, ...userData } =
        user as IUserListItemResponse & { roleId: string; roleName: string };
      const existingUser = userMap.get(user.id);

      if (existingUser) {
        if (!existingUser.roleIds.includes(roleId)) {
          existingUser.roleIds.push(roleId);
          existingUser.assignableAssignmentTemplateIds = Array.from(
            new Set(
              existingUser.assignableAssignmentTemplateIds.concat(
                roleTemplateMap[roleId] || []
              )
            )
          );
        }
      } else {
        const userAssignments = filteredAssignments.filter(
          (assignment) => assignment.assigneeId === user.id
        );
        const previousDate = subtractFromBusinessDate(date, 1);
        const { currentDateAssignments, previousDateAssignments } =
          userAssignments.reduce(
            (acc, assignment) => {
              const scheduledDate = assignment?.order?.effectiveScheduledDate;

              if (isSameDay(scheduledDate, date)) {
                acc.currentDateAssignments.push(assignment);
              } else if (isSameDay(scheduledDate, previousDate)) {
                acc.previousDateAssignments.push(assignment);
              }

              return acc;
            },
            {
              currentDateAssignments: [] as IAssignment[],
              previousDateAssignments: [] as IAssignment[],
            }
          );

        userMap.set(user.id, {
          ...userData,
          roleIds: [roleId],
          assignableAssignmentTemplateIds: roleTemplateMap[roleId] || [],
          currentDateAssignments,
          previousDateAssignments,
        });
      }
    });

    return Array.from(userMap.values());
  }, [users, date, filteredAssignments, roleTemplateMap]);
  const selectedUsers = useMemo(() => {
    if (
      activeSubTab &&
      !activeSubTab?.roleId &&
      activeSubTab?.value !== ASSIGNMENTS_ALL_OPTION
    ) {
      return [];
    }

    const sortedUsers = [...updatedUsers].sort((a, b) =>
      (a.displayName || a.firstName || "").localeCompare(
        b.displayName || b.firstName || ""
      )
    );

    if (activeSubTab && activeSubTab?.value !== ASSIGNMENTS_ALL_OPTION) {
      return sortedUsers.filter((user) =>
        user.roleIds.includes(activeSubTab.roleId as string)
      );
    }

    return sortedUsers;
  }, [activeSubTab, updatedUsers]);
  const defaultItems = useMemo(
    () => [
      {
        disabled: false,
        hidden: false,
        count: 0,
        label: ASSIGNMENTS_ALL_OPTION,
        value: ASSIGNMENTS_ALL_OPTION,
      },
      ...assignmentTemplates.map((template) => ({
        disabled: false,
        hidden: false,
        count: 0,
        label: template.label,
        value: template.value,
        roleId: template.roleId,
        roleName: template.roleName,
      })),
    ],
    [assignmentTemplates]
  );
  const tabGroups = useMemo(() => {
    const assignmentsMap = {
      [AssignmentsTab.Assigned]: currentAssignedAssignments,
      [AssignmentsTab.Unassigned]: unassignedAssignments,
      [AssignmentsTab.Published]: currentPublishedAssignments,
    };

    return Object.fromEntries(
      (Object.keys(AssignmentsTab) as AssignmentsTab[]).map((groupKey) => {
        return [
          groupKey,
          {
            ...assignmentsTabGroups[groupKey],
            disabled: false,
            items: defaultItems.map((item) => ({
              ...item,
              count: countAssignments(assignmentsMap[groupKey], item.value),
            })),
          },
        ];
      })
    ) as AssignmentsTabGroup;
  }, [
    defaultItems,
    currentAssignedAssignments,
    unassignedAssignments,
    currentPublishedAssignments,
  ]);
  const crewContainerTitle = (() => {
    if (!activeSubTab || activeSubTab?.value === ASSIGNMENTS_ALL_OPTION) {
      return `All Crews`;
    }

    return activeSubTab?.roleName || "";
  })();
  const cityOptions = useMemo(
    () => cities.map((city) => ({ label: city.cityName, value: city.id })),
    [cities]
  );
  const communityOptions = useMemo(
    () =>
      communities.map(({ id, name, city }) => ({
        label: `${name}${city?.cityName ? `, ${city.cityName}` : ""}`,
        value: id,
      })),
    [communities]
  );
  const getFilterOptions = () => {
    getCommunities();
    getCities();
  };

  const getAssignments = () => {
    stateManager.run(async () => {
      const response = await taskService.assignments.getOrderAssignments({
        assignedDate: date.toISOString(),
      });

      if (response.status === HTTP_STATUSES.OK) {
        setAssignments(response.data.assignments);
        setUsers(response.data.users);
      }
    });
  };

  const assignAssignment = useCallback(
    (assignmentId: string, assigneeId: string) => {
      if (loadingAssignmentsRef.current[assignmentId]) return;
      loadingAssignmentsRef.current[assignmentId] = true;

      stateManager.run(async () => {
        const response = await taskService.assignments.assign({
          assignmentId,
          data: { assigneeId, assignmentDate: date.toISOString() },
          modifiedOn:
            unassignedAssignments.find((a) => a.id === assignmentId)
              ?.modifiedOn || "",
          toastMessageConfig: {
            success: {
              text: "Assigned order successfully!",
            },
            failure: {
              text: "Failed to assign order",
            },
          },
        });

        if (response.status === HTTP_STATUSES.OK) {
          setAssignments((prev) =>
            prev.map((assignment) =>
              assignment.id === assignmentId
                ? {
                    ...assignment,
                    modifiedOn: response.data.modifiedOn,
                    assigneeId,
                    order: {
                      ...assignment.order,
                      effectiveScheduledDate: date.toISOString(),
                    },
                  }
                : assignment
            )
          );
        }
        loadingAssignmentsRef.current[assignmentId] = false;
      });
    },
    [date, unassignedAssignments]
  );

  const unassignAssignment = useCallback(
    (assignmentId: string) => {
      if (loadingAssignmentsRef.current[assignmentId]) return;
      loadingAssignmentsRef.current[assignmentId] = true;
      stateManager.run(async () => {
        const response = await taskService.assignments.unassign({
          assignmentId,
          modifiedOn:
            assignments.find((a) => a.id === assignmentId)?.modifiedOn || "",
          toastMessageConfig: {
            success: {
              text: "Unassigned order successfully!",
            },
            failure: {
              text: "Failed to unassign order",
            },
          },
        });

        if (response.status === HTTP_STATUSES.OK) {
          setAssignments((prev) =>
            prev.map((assignment) =>
              assignment.id === assignmentId
                ? {
                    ...assignment,
                    modifiedOn: response.data.modifiedOn,
                    assigneeId: null,
                  }
                : assignment
            )
          );
        }
        loadingAssignmentsRef.current[assignmentId] = false;
      });
    },
    [assignments]
  );

  const handleDateChange = useCallback((date: Moment) => {
    setDate(date);
    setSelectedUser(null);
  }, []);

  const handleFiltersChange = useCallback(
    (filters: string[]) => {
      setFilters(filters);
    },
    [setFilters]
  );
  const filterOptions = useMemo(
    () =>
      unassignedDisplayType === AssignmentsUnassignedDisplayType.City
        ? cityOptions
        : communityOptions,
    [unassignedDisplayType, cityOptions, communityOptions]
  );

  const handleTabChange = useCallback(
    (group: AssignmentsTab) => {
      setActiveTab(group);
      setActiveSubTab(defaultItems[0]);
      setSelectedUser(null);
    },
    [setActiveTab, defaultItems]
  );

  const handleSubItemChange = useCallback(
    (group: AssignmentsTab, _item: TabGroupItem) => {
      setActiveTab(group);
      setActiveSubTab(_item);
      setSelectedUser(null);
      if (group !== activeTab) {
        handleUnassignedDisplayModeChange(AssignmentsUnassignedDisplayMode.All);
        setUnassignedDisplayType(AssignmentsUnassignedDisplayType.City);
        setUserSortBy(AssignmentUserSortBy.Alphabetical);
      }
    },
    [activeTab, setActiveSubTab, setActiveTab]
  );

  const toggleCrewContainerCollapse = useCallback(
    () =>
      isCrewContainerCollapsed
        ? handleCrewContainerExpand()
        : handleCrewContainerCollapse(),
    [
      isCrewContainerCollapsed,
      handleCrewContainerCollapse,
      handleCrewContainerExpand,
    ]
  );

  const toggleUnassignedContainerCollapse = useCallback(
    () =>
      isUnassignedContainerCollapsed
        ? handleUnassignedContainerExpand()
        : handleUnassignedContainerCollapse(),
    [
      isUnassignedContainerCollapsed,
      handleUnassignedContainerCollapse,
      handleUnassignedContainerExpand,
    ]
  );

  const handleUnassignedDisplayModeChange = useCallback(
    (mode: AssignmentsUnassignedDisplayMode) => {
      setUnassignedDisplayMode(mode);
      setUserSortBy(AssignmentUserSortBy.Alphabetical);
      handleUnassignedContainerExpand();
      handleCrewContainerExpand();
    },
    [setUnassignedDisplayMode]
  );

  const handleUnassignedDisplayTypeChange = useCallback(
    (type: AssignmentsUnassignedDisplayType) => {
      setUnassignedDisplayType(type);
      setFilters([]);
    },
    [setUnassignedDisplayType]
  );

  const handleUserSortByChange = useCallback((sortBy: AssignmentUserSortBy) => {
    setUserSortBy(sortBy);
  }, []);

  const resetDisplayModes = useCallback(() => {
    handleUnassignedDisplayModeChange(AssignmentsUnassignedDisplayMode.All);
    setUnassignedDisplayType(AssignmentsUnassignedDisplayType.City);
    setUserSortBy(AssignmentUserSortBy.Alphabetical);
    setFilters([]);
  }, [handleUnassignedDisplayModeChange]);

  const handleSelectedUserChange = useCallback(
    (user: IAssignmentCalendarUser | null) => {
      setSelectedUser(user);
    },
    []
  );

  return {
    isLoading: stateManager.isBusy(),
    activeTab,
    activeSubTab,
    tabGroups,
    date,
    unassignedDisplayType,
    unassignedDisplayMode,
    crewContainerTitle,
    isCrewContainerCollapsed,
    isUnassignedContainerCollapsed,
    filters,
    filterOptions,
    assignments,
    unassignedAssignments: filteredUnassignedAssignments,
    userSortBy,
    users: selectedUsers,
    selectedUser,
    minHeight,
    setMinHeight,
    getAssignments,
    getFilterOptions,
    assignAssignment,
    unassignAssignment,
    handleDateChange,
    handleUnassignedDisplayTypeChange,
    handleFiltersChange,
    handleUnassignedDisplayModeChange,
    handleTabChange,
    handleSubItemChange,
    handleSelectedUserChange,
    handleUserSortByChange,
    toggleCrewContainerCollapse,
    toggleUnassignedContainerCollapse,
    resetDisplayModes,
  };
};
